import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Snackbar, Alert, Stack } from '@mui/material';
import type { AlertColor } from '@mui/material';

type NotificationSeverity = AlertColor; // 'success' | 'info' | 'warning' | 'error'

interface Notification {
  id: number;
  message: string;
  severity: NotificationSeverity;
  autoHideDuration: number | null; // null means no auto-hide
}

interface NotifyOptions {
  duration?: number | null;
}

interface AsyncMessages {
  pending: string;
  success: string;
  error?: string | ((err: unknown) => string);
}

interface NotificationContextValue {
  notify: (message: string, severity: NotificationSeverity, options?: NotifyOptions) => number;
  dismiss: (id: number) => void;
  runWithNotification: <T>(asyncFn: () => Promise<T>, messages: AsyncMessages) => Promise<T | null>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function useNotifier(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifier must be used within a NotificationProvider');
  }
  return ctx;
}

const DEFAULT_DURATIONS: Record<NotificationSeverity, number | null> = {
  success: 2500,
  info: 3000,
  warning: null, // pending notifications stay until dismissed
  error: 5000,
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const idRef = useRef(0);

  const notify = useCallback((message: string, severity: NotificationSeverity, options?: NotifyOptions): number => {
    const id = ++idRef.current;
    const duration = options?.duration === undefined ? DEFAULT_DURATIONS[severity] : options.duration;
    setNotifications((prev) => [...prev, { id, message, severity, autoHideDuration: duration }]);
    return id;
  }, []);

  const dismiss = useCallback((id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const runWithNotification = useCallback(
    async <T,>(asyncFn: () => Promise<T>, messages: AsyncMessages): Promise<T | null> => {
      const pendingId = notify(messages.pending, 'warning', { duration: null });
      try {
        const result = await asyncFn();
        dismiss(pendingId);
        notify(messages.success, 'success');
        return result;
      } catch (err) {
        dismiss(pendingId);
        const errorMessage =
          typeof messages.error === 'function'
            ? messages.error(err)
            : messages.error || `Failed: ${err instanceof Error ? err.message : String(err)}`;
        notify(errorMessage, 'error');
        return null;
      }
    },
    [notify, dismiss]
  );

  const value = useMemo(
    () => ({ notify, dismiss, runWithNotification }),
    [notify, dismiss, runWithNotification]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Stack
        spacing={1}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: (theme) => theme.zIndex.snackbar,
          maxWidth: 'min(420px, calc(100vw - 48px))',
        }}
      >
        {notifications.map((notification) => (
          <Snackbar
            key={notification.id}
            open
            autoHideDuration={notification.autoHideDuration ?? undefined}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            onClose={(_, reason) => {
              if (reason === 'clickaway') return;
              dismiss(notification.id);
            }}
            // Render inline (no Snackbar positioning) so the Stack handles layout
            sx={{ position: 'static', transform: 'none', '&.MuiSnackbar-anchorOriginBottomRight': { position: 'static' } }}
            ClickAwayListenerProps={{ onClickAway: () => undefined }}
          >
            <Alert
              elevation={6}
              variant="filled"
              severity={notification.severity}
              onClose={() => dismiss(notification.id)}
              sx={{ width: '100%' }}
            >
              {notification.message}
            </Alert>
          </Snackbar>
        ))}
      </Stack>
    </NotificationContext.Provider>
  );
};
